import { Box, Flex } from '@chakra-ui/react';
import { L3_PATHS } from 'Deer/constants/routes';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { checkEnableCompanySupport } from 'utils/helpers/featureFlags';

import { isMobileView } from 'constants/isMobileView';
import { REVIEW_INTENTS } from 'constants/moduleIntentTypes';
import { routes } from 'constants/routes';

import { MultiSelectWithCheckboxes } from 'uiToolkitV1/MultiSelectWithCheckboxes/MultiSelectWithCheckboxes';

import {
  useSelectReviewsSelectedIntent,
  useSelectedReviewCompanies,
  useAllReviewCompanies,
  setSelectedCompanies,
  useSelectedReviewLocations,
  useAllReviewLocations,
  setSelectedLocations,
} from '../slice/ReviewsIntentSlice';
import { LandingPageReviewCycleDropdown } from './LandingPageReviewCycleDropdown';
import { ReviewManageReviewCycleDropdown } from './ReviewManageReviewCycleDropdown';
import { ReviewManageTimelineFilter } from './ReviewManageTimelineFilter';

const useIsCompanyReviewPage = () => {
  const location = useLocation();
  return location.pathname.startsWith('/app/company/review');
};

const MetaFilters = () => {
  const { id: intentId } = useSelectReviewsSelectedIntent() ?? {};
  const dispatch = useDispatch();
  const isCompletionPage = intentId === REVIEW_INTENTS.REVIEW_COMPLETION_STATUS;
  const isCalibratedPage = intentId === REVIEW_INTENTS.REVIEW_CALIBRATED_RESULTS;

  const isReviewViewCalibrationPage = useRouteMatch({
    path: [L3_PATHS.team.view.review.calibrationView, L3_PATHS.company.review.calibrationView],
    exact: true,
  });

  const history = useHistory();

  const showTimelineFilter = (isCompletionPage || isCalibratedPage) && !isReviewViewCalibrationPage;

  const isCompanyReviewPage = useIsCompanyReviewPage();

  const allCompanies = useAllReviewCompanies();
  const selectedCompanies = useSelectedReviewCompanies();
  const selectedLocations = useSelectedReviewLocations();
  const allLocations = useAllReviewLocations();
  const isTalentPlanningPage = history.location.pathname.includes(routes.performanceReview.talentPlanning);

  const showCompanyFilter =
    checkEnableCompanySupport() &&
    !isTalentPlanningPage &&
    allCompanies.length > 1 &&
    isCompanyReviewPage &&
    (intentId === REVIEW_INTENTS.REVIEW_COMPLETION_STATUS || intentId === REVIEW_INTENTS.REVIEW_CALIBRATED_RESULTS);

  const showLocationFilter =
    checkEnableCompanySupport() &&
    !isTalentPlanningPage &&
    allLocations.length > 1 &&
    isCompanyReviewPage &&
    (intentId === REVIEW_INTENTS.REVIEW_COMPLETION_STATUS || intentId === REVIEW_INTENTS.REVIEW_CALIBRATED_RESULTS);

  switch (intentId) {
    case REVIEW_INTENTS.PENDING_ON_ME:
    case REVIEW_INTENTS.PENDING_ON_OTHERS:
    case REVIEW_INTENTS.COMPLETED_BY_ME:
      return (
        <Flex gap="12px" alignItems="center">
          <LandingPageReviewCycleDropdown />
        </Flex>
      );

    case REVIEW_INTENTS.REVIEW_MANAGE:
    case REVIEW_INTENTS.REVIEW_COMPLETION_STATUS:
    case REVIEW_INTENTS.REVIEW_CALIBRATED_RESULTS:
    case REVIEW_INTENTS.REVIEW_POST_REVIEW_ACTION_PLAN:
    case REVIEW_INTENTS.REVIEW_DETAILED_REPORTS:
      return (
        <Flex gap={!isMobileView() ? '12px' : undefined} flexWrap="wrap">
          <Box display="flex" alignItems="center">
            <ReviewManageReviewCycleDropdown />
          </Box>
          {showTimelineFilter && (
            <Box display="flex">
              <ReviewManageTimelineFilter />
            </Box>
          )}
          {showCompanyFilter && (
            <MultiSelectWithCheckboxes
              options={allCompanies.map(({ companyName, id }) => ({ label: companyName, value: id }))}
              selectedOptions={selectedCompanies.map(({ companyName, id }) => ({
                label: companyName,
                value: id,
              }))}
              onChange={(selected) => {
                dispatch(setSelectedCompanies(selected.map(({ label, value }) => ({ companyName: label, id: value }))));
              }}
              placeholder="Select companies"
              label="Companies"
            />
          )}
          {showLocationFilter && (
            <MultiSelectWithCheckboxes
              options={allLocations.map((locationName) => ({ label: locationName, value: locationName }))}
              selectedOptions={selectedLocations.map((locationName) => ({
                label: locationName,
                value: locationName,
              }))}
              onChange={(selected) => {
                dispatch(setSelectedLocations(selected.map(({ value }) => value)));
              }}
              placeholder="Select locations"
              label="Locations"
            />
          )}
        </Flex>
      );

    default:
      return null;
  }
};

export { MetaFilters };
